import { z } from 'zod';

/**
 * Google Business Profile (Pro, feature `google.business`). Truth rules (ARCHITECTURE §8):
 *  - reviews are fetched on demand; only a short-TTL cache exists (no warehousing);
 *  - the Google API has no reviewer email — no schema here has one (strict objects);
 *  - matching a review to a review request is a `possible_match` (heuristic, explained), never
 *    "confirmed";
 *  - replies are published only after an explicit approval; there is no auto-reply.
 */

export const GOOGLE_CONNECTION_STATUSES = [
  'connected',
  'needs_reauth',
  'revoked',
  'error',
  'disconnected',
] as const;
export type GoogleConnectionStatus = (typeof GOOGLE_CONNECTION_STATUSES)[number];

export const GOOGLE_REPLY_DRAFT_STATUSES = [
  'draft',
  'approved',
  'published',
  'discarded',
  'failed',
] as const;
export type GoogleReplyDraftStatus = (typeof GOOGLE_REPLY_DRAFT_STATUSES)[number];

export const REVIEW_MATCH_CONFIDENCE = ['low', 'medium', 'high'] as const;
export type ReviewMatchConfidence = (typeof REVIEW_MATCH_CONFIDENCE)[number];

export const REVIEW_MATCH_STATUSES = ['possible_match', 'dismissed', 'acknowledged'] as const;
export type ReviewMatchStatus = (typeof REVIEW_MATCH_STATUSES)[number];

/** GBP content cache TTL ceiling (policy: on-demand fetch, short TTL only). */
export const GOOGLE_REVIEW_CACHE_TTL_HOURS = 24;
/** Default TTL of the review cache (platform_config `google.reviewCacheTtlMinutes` overrides, ≤ ceiling). */
export const GOOGLE_REVIEW_CACHE_DEFAULT_TTL_MINUTES = 30;
/** Auto-publishing AI replies is not implemented (policy: needs prior specific, express consent). */
export const ENABLE_GOOGLE_AUTO_REPLY = false as const;
/** Google's limit for a reply comment (bytes, UTF-8). */
export const GOOGLE_REPLY_MAX_BYTES = 4096;

export const REPLY_TONES = ['warm', 'professional', 'brief'] as const;
export type ReplyTone = (typeof REPLY_TONES)[number];
export const REPLY_LANGUAGES = ['auto', 'el', 'en'] as const;
export type ReplyLanguage = (typeof REPLY_LANGUAGES)[number];

export function utf8Bytes(s: string): number {
  return new TextEncoder().encode(s).length;
}

const ReplyTextSchema = z
  .string()
  .trim()
  .min(1)
  .refine((s) => utf8Bytes(s) <= GOOGLE_REPLY_MAX_BYTES, { message: 'reply_too_long' });

// ─── connection & locations ──────────────────────────────────────────────────

export const GoogleLocationSchema = z.strictObject({
  id: z.uuid(),
  title: z.string().nullable(),
  address: z.string().nullable(),
  verified: z.boolean(),
  /** Linked to the GrowHero store (`locations.id`). */
  linkedStoreId: z.uuid().nullable(),
  mapsUri: z.string().nullable(),
  newReviewUri: z.string().nullable(),
  accountLabel: z.string().nullable(),
});
export type GoogleLocation = z.infer<typeof GoogleLocationSchema>;

export const GoogleConnectionSchema = z.strictObject({
  id: z.uuid(),
  status: z.enum(GOOGLE_CONNECTION_STATUSES),
  mode: z.enum(['fake', 'real']),
  /** Masked, e.g. `a•••@gmail.com`. */
  accountEmail: z.string().nullable(),
  accountName: z.string().nullable(),
  connectedAt: z.string().nullable(),
  lastSyncAt: z.string().nullable(),
  /** Machine-readable reason (`revoked`, `api_access_denied`, …); no provider text. */
  lastError: z.string().nullable(),
});
export type GoogleConnection = z.infer<typeof GoogleConnectionSchema>;

export const GoogleStatusResponseSchema = z.strictObject({
  mode: z.enum(['fake', 'real']),
  /** False when the real integration has no approved OAuth client configured (BLOCKED_EXTERNAL). */
  available: z.boolean(),
  unavailableReason: z.enum(['not_configured', 'fake_disabled']).nullable(),
  autoReply: z.literal(false),
  canManage: z.boolean(),
  canPublish: z.boolean(),
  connection: GoogleConnectionSchema.nullable(),
  locations: z.array(GoogleLocationSchema),
  linkedLocationId: z.uuid().nullable(),
  store: z.strictObject({ id: z.uuid(), name: z.string(), hasReviewUrl: z.boolean() }).nullable(),
});
export type GoogleStatusResponse = z.infer<typeof GoogleStatusResponseSchema>;

export const GoogleOAuthStartRequestSchema = z.strictObject({}).default({});
export const GoogleOAuthStartResponseSchema = z.strictObject({ url: z.string() });

export const GoogleOAuthCallbackRequestSchema = z.strictObject({
  code: z.string().min(1).max(2048).optional(),
  state: z.string().min(16).max(256),
  /** Google's `error` query param (e.g. access_denied) — closes the attempt without connecting. */
  error: z.string().max(100).optional(),
});

export const GoogleLinkLocationRequestSchema = z.strictObject({
  /** Also copy Google's review link into the store settings when the store has none. */
  useReviewLink: z.boolean().default(false),
});

// ─── reviews ─────────────────────────────────────────────────────────────────

export const GoogleReviewReplySchema = z.strictObject({
  comment: z.string(),
  updateTime: z.string().nullable(),
  state: z.enum(['pending', 'rejected', 'approved', 'unspecified']).nullable(),
});

export const PossibleMatchSignalsSchema = z.strictObject({
  /** How the public name compares with the customer's name. */
  nameMatch: z.enum(['full', 'first_and_initial', 'first_only', 'similar']),
  /** What we know the customer did before the review appeared. */
  basis: z.enum(['google_clicked', 'page_opened', 'sent']),
  /** Hours between that action and the review (rounded, ≥ 0). */
  hoursAfter: z.number().int().min(0),
  /** Other requests with a similar name in the same window (ambiguity). */
  competing: z.number().int().min(0),
});
export type PossibleMatchSignals = z.infer<typeof PossibleMatchSignalsSchema>;

export const PossibleMatchSchema = z.strictObject({
  id: z.uuid(),
  /** Always `possible_match` (or `dismissed`) — never confirmed. */
  status: z.enum(REVIEW_MATCH_STATUSES),
  confidence: z.enum(REVIEW_MATCH_CONFIDENCE),
  reviewRequestId: z.uuid().nullable(),
  contactId: z.uuid().nullable(),
  /** The customer's name in GrowHero (our CRM data, staff-only). */
  contactName: z.string().nullable(),
  signals: PossibleMatchSignalsSchema,
});
export type PossibleMatch = z.infer<typeof PossibleMatchSchema>;

export const GoogleReplyDraftSchema = z.strictObject({
  id: z.uuid(),
  reviewId: z.string(),
  text: z.string(),
  tone: z.enum(REPLY_TONES).nullable(),
  language: z.enum(['el', 'en']).nullable(),
  variants: z.array(
    z.strictObject({
      key: z.string(),
      text: z.string(),
      /** Links the model wrote that were removed from this variant (shown to the user). */
      removedLinks: z.array(z.string()).optional(),
    }),
  ),
  injectionFlagged: z.boolean(),
  status: z.enum(GOOGLE_REPLY_DRAFT_STATUSES),
  approvedBy: z.string().nullable(),
  approvedAt: z.string().nullable(),
  publishedAt: z.string().nullable(),
  creditsUsed: z.number().int(),
  error: z.string().nullable(),
  updatedAt: z.string(),
});
export type GoogleReplyDraft = z.infer<typeof GoogleReplyDraftSchema>;

/** A review as shown in the inbox. Strict: there is no reviewer email field, by design. */
export const GoogleReviewSchema = z.strictObject({
  id: z.string(),
  rating: z.number().int().min(1).max(5).nullable(),
  comment: z.string().nullable(),
  reviewer: z.strictObject({
    displayName: z.string().nullable(),
    profilePhotoUrl: z.string().nullable(),
    isAnonymous: z.boolean(),
  }),
  createTime: z.string(),
  updateTime: z.string().nullable(),
  reply: GoogleReviewReplySchema.nullable(),
  /** Google's language guess is not in the API; we detect Greek script locally. */
  language: z.enum(['el', 'en', 'other']),
  /** Low rating (≤ 2), or 3 stars with complaint words, without a reply. */
  urgent: z.boolean(),
  urgentReasons: z.array(z.enum(['low_rating', 'complaint_words', 'unanswered'])),
  isNew: z.boolean(),
  draft: GoogleReplyDraftSchema.nullable(),
  matches: z.array(PossibleMatchSchema),
});
export type GoogleReview = z.infer<typeof GoogleReviewSchema>;

export const GOOGLE_REVIEW_FILTERS = ['all', 'unanswered', 'urgent', 'drafts'] as const;
export type GoogleReviewFilter = (typeof GOOGLE_REVIEW_FILTERS)[number];

export const GoogleReviewsQuerySchema = z.object({
  refresh: z.enum(['1', 'true']).optional(),
  filter: z.enum(GOOGLE_REVIEW_FILTERS).default('all'),
  rating: z.coerce.number().int().min(1).max(5).optional(),
});

export const GoogleReviewSummarySchema = z.strictObject({
  averageRating: z.number().nullable(),
  totalReviewCount: z.number().int(),
  /** False when Google returned more reviews than one on-demand fetch reads. */
  complete: z.boolean(),
});

export const GoogleReviewsResponseSchema = z.strictObject({
  location: GoogleLocationSchema,
  summary: GoogleReviewSummarySchema,
  counts: z.strictObject({
    fetched: z.number().int(),
    unanswered: z.number().int(),
    urgent: z.number().int(),
    drafts: z.number().int(),
    possibleMatches: z.number().int(),
  }),
  source: z.enum(['live', 'cache']),
  fetchedAt: z.string(),
  expiresAt: z.string(),
  reviews: z.array(GoogleReviewSchema),
});
export type GoogleReviewsResponse = z.infer<typeof GoogleReviewsResponseSchema>;

// ─── drafts ──────────────────────────────────────────────────────────────────

export const CreateReplyDraftRequestSchema = z.strictObject({
  tone: z.enum(REPLY_TONES).default('warm'),
  language: z.enum(REPLY_LANGUAGES).default('auto'),
  /** Client-generated id of this user intent (a retried click is not charged twice). */
  requestId: z.string().min(8).max(100).optional(),
});

export const UpdateReplyDraftRequestSchema = z.strictObject({ text: ReplyTextSchema });
export const SaveReplyDraftRequestSchema = z.strictObject({
  text: ReplyTextSchema,
  tone: z.enum(REPLY_TONES).optional(),
});
export const ApproveReplyDraftRequestSchema = z.strictObject({ text: ReplyTextSchema });
export const PublishReplyDraftRequestSchema = z.strictObject({ confirm: z.literal(true) });
export const DeleteReplyRequestSchema = z.strictObject({ confirm: z.literal(true) });

export const ReplyDraftResponseSchema = z.strictObject({ draft: GoogleReplyDraftSchema });

// ─── summary (reviews overview hook) ────────────────────────────────────────

export const GoogleSummaryResponseSchema = z.strictObject({
  connected: z.boolean(),
  status: z.enum(GOOGLE_CONNECTION_STATUSES).nullable(),
  locationTitle: z.string().nullable(),
  /** Raw totals as reported by Google (from the live fetch/short cache; null when not fetched). */
  live: z
    .strictObject({
      averageRating: z.number().nullable(),
      totalReviewCount: z.number().int(),
      fetchedAt: z.string(),
    })
    .nullable(),
  /** Possible attributions to review requests — heuristic, reported separately from totals. */
  possibleMatches: z.strictObject({
    high: z.number().int(),
    medium: z.number().int(),
    low: z.number().int(),
  }),
  /** Google button clicks in the same period (a click, not a review). */
  googleClicks30d: z.number().int(),
});
export type GoogleSummaryResponse = z.infer<typeof GoogleSummaryResponseSchema>;
